import { Input } from "@common/components/ui/input"
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@common/components/ui/popover"
import { Separator } from "@common/components/ui/separator"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@common/components/ui/tooltip"
import { cn } from "@common/lib/utils"
import { type ComponentProps, useId, useState } from "react"

export type LookupOption = {
  id: string
  label: string
  hint?: string | null
}

type Props = Omit<
  ComponentProps<typeof Input>,
  | "value"
  | "onChange"
  | "onFocus"
  | "onKeyDown"
  | "role"
  | "autoComplete"
  | "aria-expanded"
  | "aria-controls"
  | "aria-autocomplete"
  | "aria-activedescendant"
> & {
  search: string
  options: ReadonlyArray<LookupOption>
  createLabel?: string
  createBlockedReason?: string | null
  renderAnchor?: (input: React.ReactElement, id?: string) => React.ReactElement
  onSearch: (search: string) => void
  onPick: (option: LookupOption) => void
  onCreate?: () => void
}

const CREATE = "__create__"

function Row({
  option,
  id,
  active,
  muted,
  blockedReason,
  onHover,
  onChoose,
}: {
  option: LookupOption
  id: string
  active: boolean
  muted?: boolean
  blockedReason?: string | null
  onHover: () => void
  onChoose: () => void
}): React.ReactElement {
  return (
    <div
      id={id}
      role="option"
      tabIndex={-1}
      aria-selected={active}
      aria-disabled={Boolean(blockedReason)}
      aria-description={blockedReason ?? undefined}
      className={cn(
        "col-span-2 grid cursor-pointer grid-cols-subgrid items-baseline gap-3 px-3 py-1 text-body-medium",
        active && "bg-accent",
        muted && "text-muted-foreground",
        blockedReason && "cursor-not-allowed opacity-50",
      )}
      onMouseEnter={onHover}
      onMouseDown={(event) => {
        event.preventDefault()
        onChoose()
      }}
    >
      <span className="shrink-0">{option.label}</span>
      <span className="min-w-0 truncate text-muted-foreground">
        {option.hint}
      </span>
    </div>
  )
}

function BlockedHint({
  reason,
  open,
  children,
}: {
  reason?: string | null
  open: boolean
  children: React.ReactElement
}): React.ReactElement {
  if (!reason) return children

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip open={open}>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side="bottom">{reason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export function LookupInput({
  search,
  options,
  createLabel,
  createBlockedReason,
  renderAnchor,
  className,
  onSearch,
  onPick,
  onCreate,
  onBlur,
  ...rest
}: Props): React.ReactElement {
  const listId = useId()
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)

  const term = search.trim()
  const matched = options.some(
    (option) => option.label.toLowerCase() === term.toLowerCase(),
  )
  const creatable = Boolean(onCreate && term && !matched)
  const createRow: LookupOption = {
    id: CREATE,
    label: createLabel ?? `Create "${search}"`,
  }
  const rows: ReadonlyArray<LookupOption> = creatable
    ? [...options, createRow]
    : options

  const expanded = open && rows.length > 0
  const current =
    active >= 0 && active < rows.length ? active : options.length > 0 ? 0 : -1
  const optionId = (index: number) => `${listId}-${index}`

  const close = () => {
    setOpen(false)
    setActive(-1)
  }

  const choose = (option: LookupOption) => {
    if (option.id === CREATE && createBlockedReason) return
    close()
    if (option.id === CREATE) return onCreate?.()
    onPick(option)
  }

  const move = (step: number) => {
    setOpen(true)
    const next = (expanded ? current : -1) + step
    if (next < 0) return setActive(rows.length - 1)
    setActive(next >= rows.length ? 0 : next)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      if (expanded) event.stopPropagation()
      return close()
    }
    if (rows.length === 0) return
    if (event.key === "ArrowDown") {
      event.preventDefault()
      return move(1)
    }
    if (event.key === "ArrowUp") {
      event.preventDefault()
      return move(-1)
    }
    if (event.key !== "Enter" || !expanded) return
    event.preventDefault()
    const chosen = rows[current] ?? (creatable ? createRow : undefined)
    if (chosen) choose(chosen)
  }

  const input = (
    <Input
      role="combobox"
      autoComplete="off"
      aria-expanded={expanded}
      aria-controls={expanded ? listId : undefined}
      aria-autocomplete="list"
      aria-activedescendant={
        expanded && current >= 0 ? optionId(current) : undefined
      }
      className={cn("w-full aria-invalid:text-destructive", className)}
      {...rest}
      value={search}
      onChange={(event) => {
        onSearch(event.target.value)
        setOpen(true)
        setActive(-1)
      }}
      onFocus={() => setOpen(true)}
      onKeyDown={handleKeyDown}
      onBlur={(event) => {
        close()
        onBlur?.(event)
      }}
    />
  )

  return (
    <Popover open={expanded}>
      <PopoverAnchor asChild>
        {renderAnchor ? renderAnchor(input, rest.id) : input}
      </PopoverAnchor>
      <PopoverContent
        id={listId}
        role="listbox"
        align="start"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onMouseDown={(event) => event.preventDefault()}
        className="w-[var(--radix-popover-trigger-width)] min-w-48 p-0 py-1"
      >
        {options.length > 0 && (
          <div
            role="presentation"
            onWheel={(event) => {
              event.currentTarget.scrollTop += event.deltaY
            }}
            className="grid max-h-60 grid-cols-[auto_1fr] overflow-y-auto"
          >
            {options.map((option, index) => (
              <Row
                key={option.id}
                option={option}
                id={optionId(index)}
                active={index === current}
                onHover={() => setActive(index)}
                onChoose={() => choose(option)}
              />
            ))}
          </div>
        )}
        {creatable && (
          <>
            {options.length > 0 && <Separator className="my-1" />}
            <BlockedHint
              reason={createBlockedReason}
              open={current === options.length}
            >
              <div role="presentation" className="grid grid-cols-[auto_1fr]">
                <Row
                  option={createRow}
                  id={optionId(options.length)}
                  active={current === options.length}
                  muted
                  blockedReason={createBlockedReason}
                  onHover={() => setActive(options.length)}
                  onChoose={() => choose(createRow)}
                />
              </div>
            </BlockedHint>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}
